import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Minus, Calendar, Briefcase, Mail } from 'lucide-react';
import denizImg from '../assets/team/deniz.png';
import useFooterOffset from '../hooks/useFooterVisible';
import {
  trackChatOpen, 
  trackChatClose, 
  trackChatMessage, 
  trackChatResponse, 
  trackChatError,
  trackChatTopic 
} from '../utils/analytics';

// Cloud Function URL - update this after deployment
const CHAT_FUNCTION_URL = 'https://us-central1-cndsoftware-70b14.cloudfunctions.net/chatWithDeniz';

// Helper to detect message topic for analytics
const detectTopic = (text) => {
  const lowerText = text.toLowerCase();
  if (lowerText.includes('price') || lowerText.includes('cost') || lowerText.includes('rate') || lowerText.includes('budget')) {
    return 'pricing';
  }
  if (lowerText.includes('contact') || lowerText.includes('email') || lowerText.includes('call') || lowerText.includes('meeting')) {
    return 'contact';
  }
  if (lowerText.includes('service') || lowerText.includes('do you do') || lowerText.includes('offer')) {
    return 'services';
  }
  if (lowerText.includes('portfolio') || lowerText.includes('project') || lowerText.includes('work')) {
    return 'portfolio';
  }
  if (lowerText.includes('mobile') || lowerText.includes('app') || lowerText.includes('ios') || lowerText.includes('android')) {
    return 'mobile_development';
  }
  if (lowerText.includes('web') || lowerText.includes('website') || lowerText.includes('react')) {
    return 'web_development';
  }
  if (lowerText.includes('ai') || lowerText.includes('gpt') || lowerText.includes('machine learning') || lowerText.includes('llm')) {
    return 'ai_services';
  }
  return 'general';
};

const BubbleWrapper = styled(motion.div)`
  position: fixed;
  bottom: 30px;
  right: 30px;
  z-index: 9998;
  display: flex;
  flex-direction: column;
  align-items: flex-end;

  @media (max-width: 768px) {
    bottom: 20px;
    right: 20px;
  }
`;

const BubbleButton = styled(motion.div)`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.accent};
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #000;
  box-shadow: 0 4px 20px rgba(255, 91, 58, 0.35);
  position: relative;
  z-index: 2;
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 6px 24px rgba(255, 91, 58, 0.45);
  }

  svg {
    width: 28px;
    height: 28px;
  }
`;

const Tooltip = styled(motion.div)`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  padding: 8px 16px;
  border-radius: 20px;
  color: #fff;
  font-size: 0.9rem;
  font-weight: 500;
  margin-bottom: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  white-space: nowrap;
  pointer-events: none;
`;

const ChatWindow = styled(motion.div)`
  position: absolute;
  bottom: 80px;
  right: 0;
  width: 350px;
  height: 500px;
  background: #1a1a1a;
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transform-origin: bottom right;

  @media (max-width: 480px) {
    width: calc(100vw - 40px);
    height: 60vh;
    bottom: 70px;
  }
`;

const ChatHeader = styled.div`
  padding: 16px;
  background: rgba(255, 255, 255, 0.05);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const HeaderInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Avatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid ${({ theme }) => theme.colors.accent};
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const HeaderText = styled.div`
  h4 {
    color: #fff;
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
  }
  
  span {
    color: ${({ theme }) => theme.colors.accent};
    font-size: 0.8rem;
    display: flex;
    align-items: center;
    gap: 4px;
    
    &::before {
      content: '';
      width: 6px;
      height: 6px;
      background: ${({ theme }) => theme.colors.accent};
      border-radius: 50%;
    }
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
  }
`;

const MessagesArea = styled.div`
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 3px;
  }
`;

const MessageBubble = styled.div`
  max-width: 80%;
  padding: 12px 16px;
  border-radius: 16px;
  font-size: 0.95rem;
  line-height: 1.5;
  
  ${props => props.isUser ? `
    background: ${props.theme.colors.accent};
    color: #000;
    align-self: flex-end;
    border-bottom-right-radius: 4px;
  ` : `
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
    align-self: flex-start;
    border-bottom-left-radius: 4px;
  `}
`;

const ActionButton = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: ${({ theme }) => theme.colors.accent};
  border: none;
  border-radius: 20px;
  color: #000;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  margin-top: 8px;
  align-self: flex-start;
  
  svg {
    width: 16px;
    height: 16px;
  }
  
  &:hover {
    filter: brightness(1.1);
  }
`;

const TypingIndicator = styled.div`
  background: rgba(255, 255, 255, 0.1);
  padding: 12px 16px;
  border-radius: 16px;
  border-bottom-left-radius: 4px;
  align-self: flex-start;
  display: flex;
  gap: 4px;
  
  span {
    width: 6px;
    height: 6px;
    background: rgba(255, 255, 255, 0.5);
    border-radius: 50%;
    animation: bounce 1.4s infinite ease-in-out both;
    
    &:nth-child(1) { animation-delay: -0.32s; }
    &:nth-child(2) { animation-delay: -0.16s; }
  }
  
  @keyframes bounce {
    0%, 80%, 100% { transform: scale(0); }
    40% { transform: scale(1); }
  }
`;

const InputArea = styled.form`
  padding: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  gap: 10px;
  background: rgba(255, 255, 255, 0.02);
`;

const Input = styled.input`
  flex: 1;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 24px;
  padding: 12px 20px;
  color: #fff;
  font-size: 0.95rem;
  outline: none;
  transition: all 0.2s;
  
  &:focus {
    border-color: ${({ theme }) => theme.colors.accent};
    background: rgba(255, 255, 255, 0.1);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const SendButton = styled.button`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.accent};
  border: none;
  color: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  &:not(:disabled):hover {
    transform: scale(1.05);
  }
`;

const INITIAL_MESSAGES = [
  {
    id: 1,
    text: "Hi there! 👋 I'm Deniz. How can I help you with your software needs today?",
    isUser: false,
    timestamp: new Date(),
    action: null
  }
];

const ACTION_CONFIG = {
  schedule_call: {
    label: 'Schedule a Call',
    icon: Calendar,
    href: 'https://calendly.com/d/dtbv-3sr-wjm/meet-with-cnd'
  },
  view_portfolio: {
    label: 'View Portfolio',
    icon: Briefcase,
    scrollTo: 'portfolio'
  },
  contact: {
    label: 'Contact Us',
    icon: Mail,
    scrollTo: 'contact'
  }
};

const ChatBubble = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [messages, setMessages] = useState(INITIAL_MESSAGES);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [messageCount, setMessageCount] = useState(0);
  const messagesEndRef = useRef(null);
  const footerOffset = useFooterOffset();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping, isOpen]);

  const handleToggleChat = () => {
    if (!isOpen) {
      trackChatOpen();
    } else {
      trackChatClose();
    }
    setIsOpen(!isOpen);
  };

  const handleActionClick = (action) => {
    const config = ACTION_CONFIG[action];
    if (!config) return;
    
    if (config.href) {
      window.open(config.href, '_blank');
    } else if (config.scrollTo) {
      const element = document.getElementById(config.scrollTo);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        setIsOpen(false);
      }
    }
  };

  const getBotResponse = (text) => {
    const lowerText = text.toLowerCase();
    
    if (lowerText.includes('hello') || lowerText.includes('hi') || lowerText.includes('hey')) {
      return "Hello! Great to see you here. Are you looking to build a new project?";
    }
    if (lowerText.includes('price') || lowerText.includes('cost') || lowerText.includes('rate')) {
      return "Each project is unique! I'd love to discuss your specific requirements to give you an accurate estimate. Would you like to schedule a quick call?";
    }
    if (lowerText.includes('contact') || lowerText.includes('email') || lowerText.includes('call')) {
      return "You can reach me directly at deniz@cndsoftware.com or schedule a meeting using the link in the Contact section!";
    }
    if (lowerText.includes('service') || lowerText.includes('do you do') || lowerText.includes('work')) {
      return "We specialize in full-stack web development, mobile apps, and custom software solutions. We use modern tech like React, Node.js, and Python.";
    }
    if (lowerText.includes('portfolio') || lowerText.includes('project')) {
      return "You can check out our Portfolio section to see some of our recent work, including AI-powered apps and e-commerce platforms.";
    }
    
    return "Thanks for your message! To give you the best answer, I'd recommend scheduling a quick call. Would you like the link?";
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!inputValue.trim()) return;

    const userMessage = {
      id: Date.now(),
      text: inputValue,
      isUser: true,
      timestamp: new Date()
    };

    // Track message analytics
    const isFirstMessage = messageCount === 0;
    trackChatMessage(inputValue.length, isFirstMessage);
    trackChatTopic(detectTopic(inputValue));
    setMessageCount(prev => prev + 1);

    setMessages(prev => [...prev, userMessage]);
    setInputValue("");
    setIsTyping(true);

    const startTime = Date.now();

    try {
      const response = await fetch(CHAT_FUNCTION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          messages: [...messages, userMessage] 
        }),
      });
      
      const data = await response.json();
      const responseTime = Date.now() - startTime;
      
      // Track response analytics
      trackChatResponse(data.text.length, responseTime);
      
      const botResponse = {
        id: Date.now() + 1,
        text: data.text,
        isUser: false,
        timestamp: new Date(),
        action: data.action || null
      };
      
      setMessages(prev => [...prev, botResponse]);
    } catch (error) {
      console.error("Error calling chat function:", error);
      
      // Track error
      trackChatError('api_error');
      
      // Fallback to local logic if cloud function fails (e.g. not deployed yet)
      const fallbackResponse = {
        id: Date.now() + 1,
        text: getBotResponse(userMessage.text),
        isUser: false,
        timestamp: new Date(),
        action: null
      };
      
      // Add a small delay for natural feel if falling back
      setTimeout(() => {
        setMessages(prev => [...prev, fallbackResponse]);
      }, 1000);
    } finally {
      setIsTyping(false);
    }
  };

  return (
    <BubbleWrapper
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 1, duration: 0.3 }}
      style={{ bottom: `${30 + footerOffset}px` }}
    >
      <AnimatePresence>
        {isOpen && (
          <ChatWindow
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
          >
            <ChatHeader>
              <HeaderInfo>
                <Avatar>
                  <img src={denizImg} alt="Deniz" />
                </Avatar>
                <HeaderText>
                  <h4>Deniz Eren Gencay</h4>
                  <span>Online</span>
                </HeaderText>
              </HeaderInfo>
              <CloseButton onClick={() => { trackChatClose(); setIsOpen(false); }}>
                <Minus size={20} />
              </CloseButton>
            </ChatHeader>

            <MessagesArea>
              {messages.map((msg) => (
                <React.Fragment key={msg.id}>
                  <MessageBubble isUser={msg.isUser}>
                    {msg.text}
                  </MessageBubble>
                  {msg.action && ACTION_CONFIG[msg.action] && (
                    <ActionButton
                      onClick={() => handleActionClick(msg.action)}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {React.createElement(ACTION_CONFIG[msg.action].icon, { size: 16 })}
                      {ACTION_CONFIG[msg.action].label}
                    </ActionButton>
                  )}
                </React.Fragment>
              ))}
              {isTyping && (
                <TypingIndicator>
                  <span />
                  <span />
                  <span />
                </TypingIndicator>
              )}
              <div ref={messagesEndRef} />
            </MessagesArea>

            <InputArea onSubmit={handleSend}>
              <Input
                type="text"
                placeholder="Type a message..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
              />
              <SendButton type="submit" disabled={!inputValue.trim()}>
                <Send size={20} />
              </SendButton>
            </InputArea>
          </ChatWindow>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isHovered && !isOpen && (
          <Tooltip
            initial={{ opacity: 0, x: 20, scale: 0.9 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 20, scale: 0.9 }}
          >
            Chat with Deniz
          </Tooltip>
        )}
      </AnimatePresence>

      <BubbleButton
        onClick={handleToggleChat}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        {isOpen ? <X /> : <MessageCircle />}
      </BubbleButton>
    </BubbleWrapper>
  );
};

export default ChatBubble;
